import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  db, doc, updateDoc, getDoc, serverTimestamp, arrayUnion
} from '../firebase/config';
import {
  X, CreditCard, Shield, Check, AlertCircle, Smartphone, Banknote,
  Copy, Info
} from 'lucide-react';

const PAYSTACK_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '';

const PaymentModal = ({ booking, onClose, onSuccess }) => {
  const { user, userData } = useAuth();
  const [loadingPro, setLoadingPro] = useState(true);
  const [proPaymentMethods, setProPaymentMethods] = useState(null);
  const [method, setMethod] = useState('paystack'); // 'paystack' | 'payshap' | 'cash'
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [amountPaidNow, setAmountPaidNow] = useState(0);

  // ---------- Compute amounts ----------
  const price = Number(booking?.price ?? booking?.servicePrice ?? 0);
  const depositAmount = Number(booking?.depositAmount ?? 0);
  const amountPaid = Number(booking?.amountPaid ?? 0);
  const amountDue = Number(booking?.amountDue ?? price);
  const paymentStatus = booking?.paymentStatus || 'unpaid';
  const policy = booking?.paymentPolicy || 'after';

  const computeChargeAmount = () => {
    if (paymentStatus === 'unpaid' || amountPaid === 0) {
      if (policy === 'deposit') return depositAmount;
      if (policy === 'full') return price;
      return price;
    }
    if (paymentStatus === 'deposit_paid') {
      return Math.max(0, price - amountPaid);
    }
    return Math.max(0, amountDue);
  };

  const chargeAmount = computeChargeAmount();

  // ---------- Load pro's payment methods ----------
  useEffect(() => {
    if (!booking?.professionalId) {
      setLoadingPro(false);
      return;
    }

    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', booking.professionalId));
        if (snap.exists()) {
          const data = snap.data();
          setProPaymentMethods({
            acceptPayShap: data.paymentMethods?.acceptPayShap || false,
            payshapId: data.paymentMethods?.payshapId || '',
            payshapName: data.paymentMethods?.payshapName || booking.professionalName || '',
            payshapNote: data.paymentMethods?.payshapNote || '',
            acceptCash: data.paymentMethods?.acceptCash !== false, // default true
            cashNote: data.paymentMethods?.cashNote || '',
          });
        } else {
          // Fallback: cash allowed, no PayShap
          setProPaymentMethods({
            acceptPayShap: false,
            acceptCash: true,
            payshapId: '',
            payshapName: '',
            payshapNote: '',
            cashNote: '',
          });
        }
      } catch (err) {
        console.error('Failed to load pro payment methods:', err);
        setProPaymentMethods({
          acceptPayShap: false,
          acceptCash: true,
        });
      } finally {
        setLoadingPro(false);
      }
    };

    load();
  }, [booking?.professionalId, booking?.professionalName]);

  // ---------- Set default method once we know pro's options ----------
  useEffect(() => {
    if (!proPaymentMethods) return;
    // Default: cash if no PayShap, else PayShap
    if (proPaymentMethods.acceptCash) setMethod('cash');
    else if (proPaymentMethods.acceptPayShap) setMethod('payshap');
    else setMethod('paystack');
  }, [proPaymentMethods]);

  if (!booking) return null;

  // ---------- Helpers ----------
  const formatPrice = (n) => `R${Number(n || 0).toFixed(2)}`;

  const getCopy = () => {
    if (paymentStatus === 'unpaid' && policy === 'deposit') {
      return {
        title: 'Pay Deposit',
        description: `Secure your booking with ${booking.professionalName}. The remaining balance will be collected at your appointment.`,
      };
    }
    if (paymentStatus === 'unpaid' && policy === 'full') {
      return {
        title: 'Pay in Full',
        description: `Pay ${formatPrice(price)} to confirm your booking with ${booking.professionalName}.`,
      };
    }
    if (paymentStatus === 'unpaid' && policy === 'after') {
      return {
        title: 'Pay Now',
        description: `Pay ${formatPrice(price)} to ${booking.professionalName}. No deposit was required.`,
      };
    }
    if (paymentStatus === 'deposit_paid') {
      return {
        title: 'Pay Remaining Balance',
        description: `You've already paid ${formatPrice(amountPaid)}. Complete your booking by paying the remaining ${formatPrice(chargeAmount)}.`,
      };
    }
    if (paymentStatus === 'pending_transfer') {
      return {
        title: 'Payment Sent',
        description: `Waiting for ${booking.professionalName} to confirm they received your payment.`,
      };
    }
    return {
      title: 'Complete Payment',
      description: `Pay the remaining balance for this booking.`,
    };
  };

  const copy = getCopy();

  // ---------- Compute new paymentStatus ----------
  const computeNewStatus = (newPaidTotal) => {
    if (newPaidTotal >= price - 0.01) return 'settled';
    if (policy === 'deposit' && newPaidTotal >= depositAmount - 0.01) return 'deposit_paid';
    if (policy === 'full' && newPaidTotal >= price - 0.01) return 'paid_in_full';
    return 'unpaid';
  };

  // ---------- Copy PayShap ID ----------
  const handleCopyPayShap = async () => {
    if (!proPaymentMethods?.payshapId) return;
    try {
      await navigator.clipboard.writeText(proPaymentMethods.payshapId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // ---------- Pay via Paystack ----------
  const handlePaystack = () => {
    if (!user) {
      setError('You must be signed in');
      return;
    }
    if (!PAYSTACK_KEY) {
      setError('Payment is not configured. Missing Paystack key.');
      return;
    }
    if (chargeAmount <= 0) {
      setError('Nothing to pay');
      return;
    }

    setProcessing(true);
    setError(null);

    const amountInKobo = Math.round(chargeAmount * 100);
    const reference = `skytone_booking_${booking.id}_${Date.now()}`;
    const email = userData?.email || user.email || '';

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_KEY,
      email,
      amount: amountInKobo,
      currency: 'ZAR',
      ref: reference,
      metadata: {
        custom_fields: [
          { display_name: 'Booking', variable_name: 'booking_id', value: booking.id },
          { display_name: 'Service', variable_name: 'service', value: booking.serviceTitle || booking.serviceName || '' },
          { display_name: 'Professional', variable_name: 'professional', value: booking.professionalName || '' },
        ],
      },
      callback: async (response) => {
        try {
          const newPaidTotal = amountPaid + chargeAmount;
          const newDue = Math.max(0, price - newPaidTotal);
          const newStatus = computeNewStatus(newPaidTotal);

          await updateDoc(doc(db, 'bookings', booking.id), {
            amountPaid: newPaidTotal,
            amountDue: newDue,
            paymentStatus: newStatus,
            paymentMethod: 'paystack',
            paymentHistory: arrayUnion({
              reference: response.reference || reference,
              amount: chargeAmount,
              method: 'paystack',
              paidAt: new Date().toISOString(),
              note: copy.title,
            }),
            paymentUpdatedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          setAmountPaidNow(chargeAmount);
          setSuccess(true);

          if (onSuccess) onSuccess(newStatus, newPaidTotal);

          setTimeout(() => onClose(), 2000);
        } catch (err) {
          console.error('Failed to record payment:', err);
          setError('Payment succeeded but we could not save your booking. Please contact support with reference: ' + reference);
          setProcessing(false);
        }
      },
      onClose: () => setProcessing(false),
    });

    try {
      handler.openIframe();
    } catch (err) {
      console.error('Paystack open error:', err);
      setError('Failed to open payment window. Please try again.');
      setProcessing(false);
    }
  };

  // ---------- Mark PayShap sent ----------
  const handlePayShapSent = async () => {
    if (!user) {
      setError('You must be signed in');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await updateDoc(doc(db, 'bookings', booking.id), {
        paymentMethod: 'payshap',
        paymentStatus: 'pending_transfer',
        clientMarkedSentAt: serverTimestamp(),
        paymentHistory: arrayUnion({
          method: 'payshap',
          amount: chargeAmount,
          note: 'Client marked as sent',
          markedAt: new Date().toISOString(),
        }),
        paymentUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSuccess(true);
      if (onSuccess) onSuccess('pending_transfer', 0);
      setTimeout(() => onClose(), 2200);
    } catch (err) {
      console.error('PayShap marking failed:', err);
      setError('Could not record your payment. Please try again.');
      setProcessing(false);
    }
  };

  // ---------- Confirm Cash ----------
  const handleConfirmCash = async () => {
    if (!user) {
      setError('You must be signed in');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await updateDoc(doc(db, 'bookings', booking.id), {
        paymentMethod: 'cash',
        // Leave paymentStatus as unpaid — pro marks collected later
        clientConfirmedCashAt: serverTimestamp(),
        paymentHistory: arrayUnion({
          method: 'cash',
          amount: chargeAmount,
          note: 'Client confirmed cash payment',
          markedAt: new Date().toISOString(),
        }),
        paymentUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSuccess(true);
      if (onSuccess) onSuccess('unpaid', 0);
      setTimeout(() => onClose(), 2200);
    } catch (err) {
      console.error('Cash confirmation failed:', err);
      setError('Could not save your confirmation. Please try again.');
      setProcessing(false);
    }
  };

  // ---------- Main button dispatcher ----------
  const handleMainAction = () => {
    if (method === 'paystack') return handlePaystack();
    if (method === 'payshap') return handlePayShapSent();
    if (method === 'cash') return handleConfirmCash();
  };

  const getMainButtonLabel = () => {
    if (processing) return null;
    if (method === 'paystack') return `Pay ${formatPrice(chargeAmount)}`;
    if (method === 'payshap') return `I've sent ${formatPrice(chargeAmount)}`;
    if (method === 'cash') return `Confirm Cash Payment`;
    return 'Continue';
  };

  // ============================================================
  // SUCCESS SCREEN
  // ============================================================
  if (success) {
    const isPayShap = method === 'payshap';
    const isCash = method === 'cash';

    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center shadow-2xl">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
            isPayShap ? 'bg-amber-50' : isCash ? 'bg-blue-50' : 'bg-green-50'
          }`}>
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
              isPayShap ? 'bg-amber-500' : isCash ? 'bg-blue-500' : 'bg-green-500'
            }`}>
              <Check size={28} className="text-white" strokeWidth={3} />
            </div>
          </div>
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">
            {isPayShap && 'Payment Sent'}
            {isCash && 'Cash Payment Confirmed'}
            {!isPayShap && !isCash && 'Payment Received'}
          </h2>
          <p className="text-gray-500 text-sm mb-4">
            {isPayShap && `${booking.professionalName} will confirm once they receive it.`}
            {isCash && `Bring ${formatPrice(chargeAmount)} to your appointment.`}
            {!isPayShap && !isCash && `${formatPrice(amountPaidNow)} paid to ${booking.professionalName}`}
          </p>

          <div className="bg-[#FCF8F9] rounded-2xl p-4 text-sm">
            <div className="flex justify-between mb-2">
              <span className="text-gray-500">Service</span>
              <span className="font-bold text-gray-900 truncate ml-2">
                {booking.serviceTitle || booking.serviceName}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Method</span>
              <span className="font-bold text-gray-900">
                {method === 'paystack' && 'Card'}
                {method === 'payshap' && 'PayShap'}
                {method === 'cash' && 'Cash'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // LOADING
  // ============================================================
  if (loadingPro) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60">
        <div className="bg-white rounded-3xl max-w-md w-full p-8 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-[#AF8BD4] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  // ============================================================
  // PAYMENT FORM
  // ============================================================
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-50 flex items-start justify-between shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
              <CreditCard size={20} />
            </div>
            <div className="min-w-0">
              <h2 className="font-serif font-bold text-lg text-gray-900 truncate">{copy.title}</h2>
              <p className="text-xs text-gray-400">Choose how to pay</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100 shrink-0 ml-2"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">

          {/* Amount card */}
          <div className="bg-[#F4EEFF] rounded-2xl p-4">
            <p className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-[0.15em] mb-2">
              Amount to pay
            </p>
            <p className="text-3xl font-serif font-bold text-gray-900">
              {formatPrice(chargeAmount)}
            </p>

            {policy === 'deposit' && paymentStatus === 'unpaid' && (
              <div className="mt-3 pt-3 border-t border-[#D9C6EE] space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Full price</span>
                  <span className="font-bold text-gray-900">{formatPrice(price)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Remaining at appointment</span>
                  <span className="font-bold text-gray-900">{formatPrice(price - depositAmount)}</span>
                </div>
              </div>
            )}

            {paymentStatus === 'deposit_paid' && (
              <div className="mt-3 pt-3 border-t border-[#D9C6EE] space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Already paid</span>
                  <span className="font-bold text-green-600">{formatPrice(amountPaid)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Method picker */}
          <div>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2">
              Payment method
            </p>
            <div className="space-y-2">

              {/* PayShap */}
              {proPaymentMethods?.acceptPayShap && proPaymentMethods.payshapId && (
                <button
                  type="button"
                  onClick={() => setMethod('payshap')}
                  className={`w-full text-left p-4 rounded-2xl border transition-colors ${
                    method === 'payshap'
                      ? 'border-[#EFA9B6] bg-[#FFF5F6]'
                      : 'border-gray-100 hover:border-[#FBE1E8]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                      method === 'payshap' ? 'bg-[#EFA9B6] text-white' : 'bg-[#FCF8F9] text-gray-500'
                    }`}>
                      <Smartphone size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-gray-900">PayShap</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Instant EFT from your bank app
                      </p>
                    </div>
                  </div>

                  {/* PayShap details */}
                  {method === 'payshap' && (
                    <div className="mt-3 pt-3 border-t border-[#FBE1E8] space-y-2">
                      <div className="bg-white rounded-xl p-3">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                          Send {formatPrice(chargeAmount)} to
                        </p>
                        <p className="font-mono font-bold text-sm text-gray-900 break-all">
                          {proPaymentMethods.payshapId}
                        </p>
                        {proPaymentMethods.payshapName && (
                          <p className="text-xs text-gray-500 mt-1">
                            {proPaymentMethods.payshapName}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleCopyPayShap(); }}
                          className={`mt-2 text-[11px] font-bold flex items-center gap-1 transition-colors ${
                            copied ? 'text-green-600' : 'text-[#EFA9B6] hover:underline'
                          }`}
                        >
                          <Copy size={11} />
                          {copied ? 'Copied!' : 'Copy ID'}
                        </button>
                      </div>

                      {proPaymentMethods.payshapNote && (
                        <div className="bg-white rounded-xl p-3 flex items-start gap-2">
                          <Info size={12} className="text-[#EFA9B6] shrink-0 mt-0.5" />
                          <p className="text-[11px] text-gray-600">
                            {proPaymentMethods.payshapNote}
                          </p>
                        </div>
                      )}

                      <p className="text-[11px] text-gray-500 leading-relaxed">
                        Pay from your bank app, then tap below to let {booking.professionalName} know.
                      </p>
                    </div>
                  )}
                </button>
              )}

              {/* Cash */}
              {proPaymentMethods?.acceptCash && (
                <button
                  type="button"
                  onClick={() => setMethod('cash')}
                  className={`w-full text-left p-4 rounded-2xl border transition-colors ${
                    method === 'cash'
                      ? 'border-[#EFA9B6] bg-[#FFF5F6]'
                      : 'border-gray-100 hover:border-[#FBE1E8]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                      method === 'cash' ? 'bg-[#EFA9B6] text-white' : 'bg-[#FCF8F9] text-gray-500'
                    }`}>
                      <Banknote size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-gray-900">Cash at appointment</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Bring {formatPrice(chargeAmount)} to your appointment
                      </p>
                    </div>
                  </div>

                  {method === 'cash' && proPaymentMethods.cashNote && (
                    <div className="mt-3 pt-3 border-t border-[#FBE1E8]">
                      <div className="bg-white rounded-xl p-3 flex items-start gap-2">
                        <Info size={12} className="text-[#EFA9B6] shrink-0 mt-0.5" />
                        <p className="text-[11px] text-gray-600">
                          {proPaymentMethods.cashNote}
                        </p>
                      </div>
                    </div>
                  )}
                </button>
              )}

              {/* Card via Paystack */}
              <button
                type="button"
                onClick={() => setMethod('paystack')}
                className={`w-full text-left p-4 rounded-2xl border transition-colors ${
                  method === 'paystack'
                    ? 'border-[#EFA9B6] bg-[#FFF5F6]'
                    : 'border-gray-100 hover:border-[#FBE1E8]'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                    method === 'paystack' ? 'bg-[#EFA9B6] text-white' : 'bg-[#FCF8F9] text-gray-500'
                  }`}>
                    <CreditCard size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-gray-900">Card or Instant EFT</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Secure payment via Paystack
                    </p>
                  </div>
                </div>

                {method === 'paystack' && (
                  <div className="mt-3 pt-3 border-t border-[#FBE1E8] flex items-start gap-2">
                    <Shield size={12} className="text-[#EFA9B6] shrink-0 mt-0.5" />
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                      Your card is processed securely by Paystack. Skytone never sees your card details.
                    </p>
                  </div>
                )}
              </button>

            </div>
          </div>

          {/* Booking summary */}
          <div className="bg-[#FCF8F9] rounded-2xl p-3">
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-gray-500">Service</span>
              <span className="font-bold text-gray-900 truncate ml-2">
                {booking.serviceTitle || booking.serviceName}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Professional</span>
              <span className="font-bold text-gray-900 truncate ml-2">
                {booking.professionalName}
              </span>
            </div>
          </div>

          {/* No methods available */}
          {!proPaymentMethods?.acceptPayShap && !proPaymentMethods?.acceptCash && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 flex items-start gap-2">
              <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                This pro hasn't set up PayShap or Cash. Use card payment.
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-100 rounded-2xl p-3 flex items-start gap-2">
              <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-600 leading-relaxed">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 pt-4 border-t border-gray-50 shrink-0">
          <button
            onClick={handleMainAction}
            disabled={processing || chargeAmount <= 0}
            className="w-full bg-[#010101] text-white py-4 rounded-full font-bold text-sm hover:bg-black/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {processing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </>
            ) : (
              <>
                {method === 'paystack' && <CreditCard size={16} />}
                {method === 'payshap' && <Smartphone size={16} />}
                {method === 'cash' && <Banknote size={16} />}
                {getMainButtonLabel()}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;